import * as React from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronUp, Loader2, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ActivityActionButton } from "./ActivityActionButton";
import { FinishAdjustmentDialog } from "./FinishAdjustmentDialog";
import type { ActiveActivity, UserInfo } from "./TaskActionsCell";
import { useNewTaskStatusMutations } from "../hooks";
import {
  canCreateAdjustment,
  getMatterMembershipRole,
  adjustmentFinishBlockKey,
} from "../utils";
import type { Task } from "../types";

export interface AdjustmentRowActionsProps {
  task: Task;
  activeActivity?: ActiveActivity | null;
  userInfo?: UserInfo | null;
  hasAccess: (resource: string, action: string) => boolean;
  expandedTasks: Record<number, boolean>;
  loadingTasks: Record<number, boolean>;
  onToggleExpand?: (taskId: number) => void;
  onStartActivity?: () => void;
  onStopActivity?: () => void;
  onActionSuccess?: () => void;
}

/**
 * Action cell for a standalone ADJUSTMENT row (task.isAdjustment === true) on the
 * task boards. Mirrors the actions available inside the expanded `AdjustmentActivities`
 * card — Finish / Edit result / Delete — plus Start/Stop so the owner can log time
 * directly from the row. Non-owners get a read-only row (just the expand chevron);
 * the parent original task is reviewed/forwarded from its own row, never from here.
 */
export function AdjustmentRowActions({
  task,
  activeActivity,
  userInfo,
  hasAccess,
  expandedTasks,
  loadingTasks,
  onToggleExpand,
  onStartActivity,
  onStopActivity,
  onActionSuccess,
}: AdjustmentRowActionsProps) {
  const { t } = useTranslation();
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const { deleteAdjustmentMutation } = useNewTaskStatusMutations();

  const roleType = userInfo?.role?.type;
  const isMd = roleType === "MD";
  const isOwner = task.assignedStaffId === userInfo?.id;
  const isFinished = task.adjustmentPercent != null;
  const isTerminal = task.newStatus === "DONE" || task.newStatus === "BILLED";
  const matterRole = getMatterMembershipRole(task.matter, userInfo?.id);

  const isExpanded = expandedTasks[task.id] ?? false;
  const isLoadingActivities = loadingTasks[task.id] ?? false;
  const isActive = activeActivity?.taskId === task.id;

  // Owner may log time while the adjustment isn't terminal; finishing only records
  // the % changed, it doesn't lock further work.
  const showStartStop = isOwner && !isTerminal;
  // Finish is THE action on a standalone adjustment row (user decision
  // 2026-06-07: no separate "Edit result" here) — shown to the owner while the
  // task is in-flight, whether or not the adjustment was already finished. An
  // adjustment row only appears on My Tasks while its mirrored status is in the
  // owner's actionable set (lead: LEAD_PENDING/PARTNER_REJECT, partner:
  // PARTNER_PENDING/BILLING_REJECT), so a rejected-back finished adjustment
  // re-offers Finish: the activity gate below keeps it disabled until fresh
  // (non-REJECT) work is logged, and re-submitting re-runs the backend
  // auto-forward (PARTNER_REJECT → PARTNER_PENDING, BILLING_REJECT →
  // BILLING_PENDING). Already-finished rows pre-fill the recorded % + comment.
  const showFinish = isOwner && !isTerminal;
  // Delete: MD may remove any adjustment (while not terminal); the owner may remove
  // their own while the parent is in a status they review (canCreateAdjustment maps
  // the adjustment's mirrored status to the create/delete window).
  const showDelete =
    !isTerminal &&
    (isMd || (isOwner && canCreateAdjustment(roleType, task.newStatus, matterRole)));

  const finishBlockKey = adjustmentFinishBlockKey(task.activities);

  // Read-only when the matter is closed or its contract is closed/terminated.
  // `phase` is the matter lifecycle key (legacy `status` is dead — Stage 14).
  const matterClosed = task.matter?.phase === "CLOSED";
  const contractClosed =
    task.matter?.contract?.status === "CLOSED" ||
    task.matter?.contract?.status === "TERMINATED";
  const showClosed = matterClosed || contractClosed;

  const handleDelete = () => {
    deleteAdjustmentMutation.mutate(task.id, {
      onSuccess: () => {
        setDeleteOpen(false);
        onActionSuccess?.();
      },
    });
  };

  return (
    <div className="flex gap-1.5 items-center flex-nowrap whitespace-nowrap">
      {/* Expand/Collapse the adjustment's own activities */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onToggleExpand?.(task.id)}
        className="h-8 w-8 p-0"
        disabled={isLoadingActivities}
      >
        {isLoadingActivities ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : isExpanded ? (
          <ChevronUp className="h-4 w-4" />
        ) : (
          <ChevronDown className="h-4 w-4" />
        )}
      </Button>

      {showClosed && (
        <span className="text-xs italic text-muted-foreground">
          {matterClosed ? t("matterClosed") : t("contractClosed")}
        </span>
      )}

      {/* Stop Activity */}
      {!showClosed && showStartStop && isActive && hasAccess("activity", "stop") && (
        <ActivityActionButton
          task={task}
          activityId={activeActivity!.id}
          onSuccess={onStopActivity}
          isStop
        />
      )}

      {/* Start Activity */}
      {!showClosed && showStartStop && !isActive && hasAccess("activity", "start") && (
        <ActivityActionButton task={task} onSuccess={onStartActivity} />
      )}

      {/* Finish Adjustment (pre-filled with the recorded result when re-finishing) */}
      {!showClosed && showFinish && (
        <FinishAdjustmentDialog
          task={task}
          disabledReason={finishBlockKey ? t(finishBlockKey) : undefined}
          {...(isFinished
            ? {
                initialPercent: task.adjustmentPercent ?? 0,
                initialComment: task.comment ?? "",
              }
            : {})}
          onSuccess={onActionSuccess}
        />
      )}

      {/* Delete Adjustment */}
      {!showClosed && showDelete && (
        <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
          <AlertDialogTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
              aria-label={t("adjustment.delete")}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("adjustment.deleteTitle")}</AlertDialogTitle>
              <AlertDialogDescription>
                {t("adjustment.deleteDescription")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={deleteAdjustmentMutation.isPending}
                className="bg-red-600 hover:bg-red-700"
              >
                {deleteAdjustmentMutation.isPending ? t("deleting") : t("delete")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}
